/**
 * First-touch attribution
 * ------------------------
 * Captura las UTMs (y cmpid) la PRIMERA vez que un visitante llega al sitio
 * y las persiste en sessionStorage. Cualquier evento o envío de formulario
 * posterior — incluso después de navegar a páginas internas sin parámetros —
 * usa estos valores como fuente de verdad.
 *
 * Reglas:
 * - Si la URL actual trae UTMs nuevas, sobrescriben (last-touch wins por sesión
 *   nueva, pero dentro de la misma sesión la primera gana salvo que llegue otro
 *   set explícito desde una campaña).
 * - sessionStorage = vive lo que dure la pestaña/sesión del navegador.
 */

const STORAGE_KEY = "_qdm_attribution_v1";

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

const EXTRA_KEYS = ["cmpid"] as const;

export type AttributionData = Partial<
  Record<(typeof UTM_KEYS)[number] | (typeof EXTRA_KEYS)[number], string>
>;

const readFromUrl = (): AttributionData => {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const data: AttributionData = {};
  [...UTM_KEYS, ...EXTRA_KEYS].forEach((k) => {
    const v = params.get(k);
    if (v) data[k] = v;
  });
  return data;
};

const readFromStorage = (): AttributionData => {
  if (typeof window === "undefined") return {};
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const writeToStorage = (data: AttributionData) => {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Silent fail (modo privado, storage lleno, etc.)
  }
};

/**
 * Inicializa el first-touch attribution. Llamar UNA vez al montar la app.
 * - Si ya había attribution guardado, NO lo sobrescribe (a menos que la URL
 *   actual traiga UTMs nuevas que indiquen una nueva campaña entrante).
 */
export const initAttribution = (): AttributionData => {
  const fromUrl = readFromUrl();
  const stored = readFromStorage();

  // Si la URL trae UTMs (nueva campaña entrante), las priorizamos y guardamos.
  if (Object.keys(fromUrl).length > 0) {
    writeToStorage(fromUrl);
    return fromUrl;
  }

  // Si ya había stored, lo conservamos como first-touch.
  return stored;
};

/**
 * Devuelve las UTMs persistidas (first-touch) para usar en eventos / envíos.
 * Fallback a la URL actual por si initAttribution aún no corrió.
 */
export const getAttribution = (): AttributionData => {
  const stored = readFromStorage();
  if (Object.keys(stored).length > 0) return stored;
  return readFromUrl();
};
